import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Target, Zap, TrendingUp, AlertCircle,
  CheckCircle2, Calendar, ChevronDown, ChevronUp,
  ArrowUpRight,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const CAL_URL = "https://cal.com/jigesh-shah-ryvr/30min";

const AREA_META = {
  brand:    { label: "Brand",             icon: Target,    color: "#7C3AED", light: "#A78BFA", bg: "#7C3AED12" },
  marketing:{ label: "Marketing",         icon: Zap,       color: "#00B4A0", light: "#00C9B3", bg: "#00B4A012" },
  revops:   { label: "Revenue Operations",icon: TrendingUp, color: "#F59E0B", light: "#FCD34D", bg: "#F59E0B12" },
};

const EFFORT_COLOR  = { low: "#10B981", medium: "#F59E0B", high: "#EF4444" };
const IMPACT_COLOR  = { low: "#52525B", medium: "#F59E0B", high: "#10B981" };
const PRIORITY_COLORS = ["#EF4444","#F97316","#F59E0B","#A78BFA","#00B4A0","#10B981"];

// ── Score ring ────────────────────────────────────────────────────────────────
function ScoreRing({ score, color, size = 80 }) {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth="6" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        fill="#111827" fontSize={size < 80 ? 14 : 18} fontWeight="bold">{score}</text>
    </svg>
  );
}

// ── Dimension bar ─────────────────────────────────────────────────────────────
function DimBar({ label, score, color }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500 capitalize">{label.replace(/_/g, " ")}</span>
        <span style={{ color }}>{score}</span>
      </div>
      <div className="h-1.5 bg-[#1E1E28] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

// ── Area panel ────────────────────────────────────────────────────────────────
function AreaPanel({ area, score, dimensions, insight }) {
  const { label, icon: Icon, color, bg } = AREA_META[area];
  const band = score >= 80 ? "Strong" : score >= 65 ? "Developing" : score >= 45 ? "Building" : "Critical";
  const bandColor = { Strong: "#10B981", Developing: "#F59E0B", Building: "#F97316", Critical: "#EF4444" }[band];

  // Filter dimensions belonging to this area
  const DIM_MAP = {
    brand:     ["positioning","voice","content","differentiation","messaging"],
    marketing: ["demand_gen","attribution","icp","martech","smarketing"],
    revops:    ["crm","forecasting","sales_process","pipeline","retention"],
  };
  const areaDims = DIM_MAP[area] || [];
  const filteredDims = Object.entries(dimensions || {}).filter(([k]) => areaDims.includes(k));

  return (
    <div className="rounded-2xl border border-gray-100 overflow-hidden" style={{ background: bg }}>
      <div className="p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border"
             style={{ background: `${color}20`, borderColor: `${color}40` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-white">{label}</h3>
            <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ color: bandColor, background: `${bandColor}15` }}>{band}</span>
          </div>
          {insight && <p className="text-xs text-gray-500 leading-relaxed mb-4">{insight}</p>}
        </div>
      </div>
      <div className="px-5 pb-5 flex gap-6 items-center">
        <ScoreRing score={score} color={color} size={80} />
        <div className="flex-1 space-y-2.5">
          {filteredDims.map(([dim, s]) => (
            <DimBar key={dim} label={dim} score={s} color={color} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Recommendation card ───────────────────────────────────────────────────────
function RecCard({ rec, idx }) {
  const { label, color } = AREA_META[rec.area] || { label: rec.area, color: "#8A8F98" };
  const [open, setOpen] = useState(idx === 0);

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
              style={{ background: PRIORITY_COLORS[idx] || "#52525B" }}>
          {idx + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color, background: `${color}15` }}>{label}</span>
            <span className="text-[10px] font-mono text-gray-400">
              Effort: <span style={{ color: EFFORT_COLOR[rec.effort] }}>{rec.effort}</span>
            </span>
            <span className="text-[10px] font-mono text-gray-400">
              Impact: <span style={{ color: IMPACT_COLOR[rec.impact] }}>{rec.impact}</span>
            </span>
          </div>
          <p className="text-sm font-semibold text-white">{rec.title}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <p className="text-sm text-gray-700 leading-relaxed">{rec.description}</p>
        </div>
      )}
    </div>
  );
}

// ── Action plan phase ─────────────────────────────────────────────────────────
function ActionPhase({ phase, label, items }) {
  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono text-gray-400 uppercase tracking-wider">{phase}</span>
        <span className="text-xs font-semibold text-white">{label}</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
            <ArrowUpRight className="w-3.5 h-3.5 text-[#00B4A0] flex-shrink-0 mt-0.5" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ResultsPage() {
  const { aid } = useParams();
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab]     = useState("dashboard"); // dashboard | action_plan

  useEffect(() => {
    axios.get(`${API}/api/assessment/${aid}`)
      .then(r => setData(r.data))
      .catch(() => setError("Could not load results. The link may have expired."))
      .finally(() => setLoading(false));
  }, [aid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4">
        <Loader2 className="w-8 h-8 text-[#00B4A0] animate-spin" />
        <p className="text-white">Loading your results…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4">
        <AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-white">{error || "Something went wrong."}</p>
      </div>
    );
  }

  const { scores, analysis, band, type_label, company_name, respondent_name } = data;
  const recs  = analysis?.recommendations || [];
  const dims  = scores?.dimensions || {};

  // Research-backed 4-phase action plan
  const phases = [
    {
      phase: "Days 1–30",
      label: "Diagnose & Define",
      items: [
        "Create a shared revenue dictionary — lead stages, pipeline stages, lifecycle statuses",
        "Assign ownership to every funnel stage and KPI",
        "Establish a single revenue scorecard (CAC, pipeline coverage, NRR, forecast accuracy)",
        "Audit your current tech stack for underused or duplicated tools",
      ],
    },
    {
      phase: "Days 31–90",
      label: "Process Control",
      items: [
        "Implement stage-exit criteria across your sales process",
        "Set lead-routing rules and SLA targets between marketing and sales",
        "Launch weekly cross-functional revenue reviews",
        "Begin stack rationalisation — eliminate shelfware",
      ],
    },
    {
      phase: "Days 91–180",
      label: "Monetisation & Planning",
      items: [
        "Formalise forecast calls and pipeline commit process",
        "Introduce pricing governance — discount approval, promo holdouts",
        "Build retention and expansion playbooks (health scoring, QBRs)",
        "Deploy attribution reporting across all demand gen channels",
      ],
    },
    {
      phase: "Days 181–365",
      label: "AI & Scale",
      items: [
        "Pilot AI in low-regret use cases: call summarisation, lead scoring, forecast anomalies",
        "Introduce propensity models for churn risk and expansion opportunity",
        "Automate routine reporting and CRM hygiene tasks",
        "Scale next-best-action and routing into your GTM motion",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white px-6 py-4 flex items-center justify-between">
        <img src="/ryvr-logo.png" alt="RYVR" className="h-7 w-auto" />
        <a href={CAL_URL} target="_blank" rel="noopener noreferrer"
           className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-[#00B4A0] transition-colors">
          <Calendar className="w-3.5 h-3.5" /> Book a deep-dive call
        </a>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero scores */}
        <div className="mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-1">{type_label} · RevOps Assessment</p>
              <h1 className="text-2xl font-bold text-gray-900">{company_name || "Your Company"}</h1>
              {respondent_name && <p className="text-sm text-gray-400 mt-0.5">Completed by {respondent_name}</p>}
            </div>
            <div className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 bg-white">
              <ScoreRing score={scores.overall} color={band?.color || "#00B4A0"} size={90} />
              <div>
                <p className="text-xs font-mono text-gray-400 mb-1">Overall RevOps Health</p>
                <p className="text-xl font-bold" style={{ color: band?.color }}>{band?.label}</p>
                <p className="text-xs text-gray-400 max-w-[180px] mt-1 leading-relaxed">{band?.desc}</p>
              </div>
            </div>
          </div>

          {/* Narrative */}
          {analysis?.narrative && (
            <div className="p-4 rounded-xl border border-gray-100 bg-white text-sm text-gray-700 leading-relaxed">
              {analysis.narrative}
            </div>
          )}
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 mb-6 border-b border-gray-100">
          {[
            { id: "dashboard",   label: "Dashboard" },
            { id: "recommendations", label: `Recommendations (${recs.length})` },
            { id: "action_plan", label: "Action Plan" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px
                ${tab === t.id ? "border-[#00B4A0] text-white" : "border-transparent text-gray-400 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Dashboard tab */}
        {tab === "dashboard" && (
          <div className="space-y-4">
            {/* Quick score row */}
            <div className="grid grid-cols-3 gap-4 mb-2">
              {Object.entries(AREA_META).map(([area, { label, color }]) => (
                <div key={area} className="p-4 rounded-xl border border-gray-100 bg-white text-center">
                  <p className="text-xs font-mono text-gray-400 mb-2">{label}</p>
                  <ScoreRing score={scores[area] || 0} color={color} size={64} />
                </div>
              ))}
            </div>
            {/* Area panels */}
            {Object.keys(AREA_META).map(area => (
              <AreaPanel
                key={area}
                area={area}
                score={scores[area] || 0}
                dimensions={dims}
                insight={analysis?.[`${area}_insight`]}
              />
            ))}
          </div>
        )}

        {/* Recommendations tab */}
        {tab === "recommendations" && (
          <div className="space-y-3">
            {recs.length > 0 ? (
              recs.map((rec, i) => <RecCard key={i} rec={rec} idx={i} />)
            ) : (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-3" />
                <p>No recommendations generated — try submitting the assessment again.</p>
              </div>
            )}
          </div>
        )}

        {/* Action plan tab */}
        {tab === "action_plan" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl border border-gray-100 bg-white mb-2">
              <p className="text-sm text-gray-500 leading-relaxed">
                This is your RevOps implementation roadmap — a research-backed 4-phase sequence.
                The right order matters: standardise before you automate, instrument before you scale AI.
              </p>
            </div>
            {phases.map(p => <ActionPhase key={p.phase} {...p} />)}

            {/* CTA */}
            <div className="mt-6 p-6 rounded-2xl border border-[#7C3AED]/30 bg-[#7C3AED]/10 text-center">
              <p className="font-bold text-white mb-1">Want a consultant to build this plan with you?</p>
              <p className="text-sm text-gray-500 mb-4">
                A 30-min call with Jigesh to turn these findings into a prioritised engagement plan.
              </p>
              <a
                href={CAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold transition-colors"
              >
                <Calendar className="w-4 h-4" /> Book a 30-min call
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
